import { StageStepper } from '@/Client/Widgets';
import type { ReactNode } from 'react';
import { isChangeOrderSettled, type ProjectLifecycleStateType } from '../../Layout';
import { DEFAULT_PROJECT_STAGE_STEPPER_CONFIG, type ProjectStageStepperConfig } from './projectStageStepperConfig';

export const ProjectStageStepper = ({
  state,
  config = DEFAULT_PROJECT_STAGE_STEPPER_CONFIG,
  onStageClick,
  renderTurnAccessory,
}: {
  state: ProjectLifecycleStateType;
  config?: ProjectStageStepperConfig;
  onStageClick?: (stageId: string) => void;
  renderTurnAccessory?: (measurement: boolean) => ReactNode;
}) => {
  // A single-stage config (e.g. SCOPE_LOCKED_PROJECT_STAGE_STEPPER_CONFIG)
  // represents a terminal state - there's no "active" track left to track
  // progress toward, so its one stage is always rendered done, never active.
  if (config.length === 1) {
    return (
      <StageStepper
        stages={config.map(stage => ({ id: stage.id, name: stage.name, sub: stage.sub(state) }))}
        activeStageId=''
        doneStageIds={[config[0].id]}
        onStageClick={onStageClick}
      />
    );
  }

  // Work stays the active track only once it has genuinely started on a settled
  // approved scope; a change order that is drafted or in review keeps scope
  // active even while work on the prior scope continues, since the scope track
  // is what needs attention.
  const activeStageId = state.phase === 'closeout'
    ? 'closeout'
    : state.phase === 'approved' && isChangeOrderSettled(state) && state.workStarted
    ? 'work'
    : 'scope';
  const stageIndex = config.findIndex(stage => stage.id === activeStageId);
  const doneStageIds = stageIndex > 0
    ? config.slice(0, stageIndex).map(stage => stage.id)
    : [];
  const stages = config.map(stage => ({
    id: stage.id,
    name: stage.name,
    sub: stage.sub(state),
    // The current holder remains visible beside whichever project track is
    // active, including closeout.
    renderAccessory: stage.id === activeStageId ? renderTurnAccessory : undefined,
  }));

  return (
    <StageStepper
      stages={stages}
      activeStageId={activeStageId}
      doneStageIds={doneStageIds}
      onStageClick={onStageClick}
    />
  );
};
